import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Paper,
  Avatar,
  Stack,
  LinearProgress,
  IconButton,
  Divider,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  SignalCellularAlt as SignalIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import axios from 'axios';
import API_URLS from '../config/api';
import { axiosConfig } from '../utils/csrf';

const SignalDashboard = () => {
  const [recentSignals, setRecentSignals] = useState([]);
  const [aggregations, setAggregations] = useState([]);
  const [stats, setStats] = useState({
    total_signals_today: 0,
    active_aggregations: 0,
    confirmed_today: 0,
    expired_today: 0,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [signalsRes, aggregationsRes] = await Promise.all([
        axios.get(API_URLS.SIGNALS_LIST, { params: { limit: 20 }, ...axiosConfig() }),
        axios.get(API_URLS.AGGREGATIONS_LIST, { params: { status: 'pending' }, ...axiosConfig() }),
      ]);

      setRecentSignals(signalsRes.data.signals || []);
      setAggregations(aggregationsRes.data.aggregations || []);
      setStats(signalsRes.data.stats || stats);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const seconds = Math.floor((new Date() - new Date(timestamp)) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const getProgressPercentage = (aggregation) => {
    const received = aggregation.received_count || 0;
    const required = aggregation.required_count || 1;
    return Math.min((received / required) * 100, 100);
  };

  const StatCard = ({ title, value, icon: Icon, gradient, shadowColor }) => (
    <Card sx={{
      borderRadius: 3,
      boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
      border: '1px solid #e2e8f0',
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.3s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        width: '100px',
        height: '100px',
        background: gradient,
        opacity: 0.1,
        borderRadius: '50%',
        transform: 'translate(30%, -30%)',
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
              {title}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#1e293b' }}>
              {value}
            </Typography>
          </Box>
          <Avatar sx={{ 
            width: 64,
            height: 64,
            background: gradient,
            boxShadow: shadowColor, // Use the shadowColor prop instead of alpha()
          }}>
            <Icon sx={{ fontSize: 32 }} />
          </Avatar>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1600, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 800, 
              color: '#1e293b',
              mb: 1,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Live Signal Monitor
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Real-time algorithm signal tracking and aggregation
          </Typography>
        </Box>
        <IconButton
          onClick={fetchDashboardData}
          disabled={loading}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            width: 48,
            height: 48,
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)',
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              transform: 'rotate(180deg)',
            },
            transition: 'all 0.3s',
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Signals Today"
            value={stats.total_signals_today}
            icon={SignalIcon}
            gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            shadowColor="0 8px 24px rgba(102, 126, 234, 0.3)"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Active Aggregations"
            value={stats.active_aggregations}
            icon={ScheduleIcon}
            gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
            shadowColor="0 8px 24px rgba(245, 158, 11, 0.3)"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Confirmed Today"
            value={stats.confirmed_today}
            icon={CheckCircleIcon}
            gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
            shadowColor="0 8px 24px rgba(16, 185, 129, 0.3)"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Expired Today"
            value={stats.expired_today}
            icon={CancelIcon}
            gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
            shadowColor="0 8px 24px rgba(239, 68, 68, 0.3)"
          />
        </Grid>
      </Grid>

      {/* Content Grid */}
      <Grid container spacing={3}>
        {/* Active Aggregations */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{
            borderRadius: 3,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
          }}>
            <Box sx={{ 
              p: 3, 
              borderBottom: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)',
                }}>
                  <SpeedIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    Active Aggregations
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending signal confirmations
                  </Typography>
                </Box>
              </Stack>
            </Box>
            <Box sx={{ p: 3, maxHeight: 600, overflowY: 'auto' }}>
              <Stack spacing={2}>
                {aggregations.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Avatar sx={{
                      width: 80,
                      height: 80,
                      background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                      color: '#94a3b8',
                      mx: 'auto',
                      mb: 2,
                    }}>
                      <SpeedIcon sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
                      No active aggregations
                    </Typography>
                  </Box>
                ) : (
                  aggregations.map((agg) => (
                    <Card key={agg.id} sx={{
                      borderRadius: 2,
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                        transform: 'translateY(-2px)',
                      }
                    }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
                              {agg.strategy_name}
                            </Typography>
                            <Stack direction="row" spacing={1}>
                              <Chip 
                                label={agg.ticker} 
                                size="small"
                                sx={{ 
                                  fontWeight: 600,
                                  background: '#f1f5f9',
                                  color: '#64748b',
                                }}
                              />
                              <Chip 
                                label={agg.action?.toUpperCase()} 
                                size="small"
                                sx={{ 
                                  fontWeight: 600,
                                  background: agg.action === 'buy' ? '#dcfce7' : '#fee2e2',
                                  color: agg.action === 'buy' ? '#166534' : '#991b1b',
                                }}
                              />
                            </Stack>
                          </Box>
                          <Chip
                            label={agg.status?.toUpperCase()}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                              color: '#92400e',
                              border: '1px solid #fbbf24',
                            }}
                          />
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Progress: {agg.received_count || 0}/{agg.required_count || 0} signals
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              fontWeight: 700,
                              color: '#667eea',
                            }}>
                              {Math.round(getProgressPercentage(agg))}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={getProgressPercentage(agg)}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              background: '#e2e8f0',
                              '& .MuiLinearProgress-bar': {
                                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: 4,
                              }
                            }}
                          />
                        </Box>

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {agg.received_algos?.map((algo, idx) => (
                            <Chip
                              key={idx}
                              label={algo}
                              size="small"
                              icon={<CheckCircleIcon sx={{ fontSize: '0.9rem' }} />}
                              sx={{
                                background: '#dcfce7',
                                color: '#166534',
                                border: '1px solid #bbf7d0',
                                fontWeight: 600,
                              }}
                            />
                          ))}
                          {agg.pending_algos?.map((algo, idx) => (
                            <Chip
                              key={idx}
                              label={algo}
                              size="small"
                              sx={{
                                background: '#f1f5f9',
                                color: '#94a3b8',
                                border: '1px solid #e2e8f0',
                                fontWeight: 600,
                              }}
                            />
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Stack>
            </Box>
          </Paper>
        </Grid>

        {/* Recent Signals */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{
            borderRadius: 3,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            height: '100%',
          }}>
            <Box sx={{ 
              p: 3, 
              borderBottom: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ 
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
                }}>
                  <SignalIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    Recent Signals
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Live feed
                  </Typography>
                </Box>
              </Stack>
            </Box>
            <Box sx={{ overflowY: 'auto', maxHeight: 540 }}>
              <Stack>
                {recentSignals.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                    <Avatar sx={{
                      width: 64,
                      height: 64,
                      background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                      color: '#94a3b8',
                      mx: 'auto',
                      mb: 2,
                    }}>
                      <SignalIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
                      No signals received
                    </Typography>
                  </Box>
                ) : (
                  recentSignals.map((signal, idx) => (
                    <Box
                      key={signal.id}
                      sx={{
                        p: 2,
                        borderBottom: idx !== recentSignals.length - 1 ? '1px solid #f1f5f9' : 'none',
                        transition: 'all 0.2s',
                        '&:hover': {
                          background: '#f8fafc',
                        }
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.25)',
                          }}
                        >
                          {signal.algo_type?.substring(0, 2).toUpperCase() || 'S'}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 700, 
                              color: '#1e293b',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {signal.script_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {formatTimeAgo(signal.received_at)}
                          </Typography>
                        </Box>
                        <Chip
                          label={signal.action?.toUpperCase()}
                          size="small"
                          icon={signal.action === 'buy' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                          sx={{
                            fontWeight: 700,
                            background: signal.action === 'buy' 
                              ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)'
                              : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                            color: signal.action === 'buy' ? '#166534' : '#991b1b',
                            border: `1px solid ${signal.action === 'buy' ? '#86efac' : '#fca5a5'}`,
                          }}
                        />
                      </Stack>
                      {signal.ticker && (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#64748b',
                            fontWeight: 600,
                            ml: 6.5,
                          }}
                        >
                          {signal.ticker} {signal.price && `@ $${parseFloat(signal.price).toFixed(2)}`}
                        </Typography>
                      )}
                    </Box>
                  ))
                )}
              </Stack>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SignalDashboard;
